import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import useMobileModalMode from '../../hooks/useMobileModalMode';
import { RECONCILIATION_CURRENCIES } from '../../utils/reconciliationUtils';
import { formatRecordDate } from '../../utils/dateUtils';

const ImportSellersModal = ({ isOpen, onClose, onRefresh }) => {
    const [candidates, setCandidates] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [defaultCurrency, setDefaultCurrency] = useState('USD');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useMobileModalMode(isOpen);

    const fetchCandidates = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await api.get('/reconciliations/companies/import-candidates');
            setCandidates(response.data || []);
            setSelected(new Set());
        } catch (error) {
            toast.error('Aday firma listesi yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        if (isOpen) {
            fetchCandidates();
        }
    }, [isOpen, fetchCandidates]);

    const toggleOne = (name) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name); else next.add(name);
            return next;
        });
    };

    const toggleAll = () => {
        setSelected((prev) => (prev.size === candidates.length ? new Set() : new Set(candidates.map((c) => c.name))));
    };

    const handleImport = async () => {
        if (selected.size === 0) {
            toast.error('En az bir firma seçin.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await api.post('/reconciliations/companies/import', {
                names: Array.from(selected),
                defaultCurrency
            });
            toast.success(response.data.message);
            if (onRefresh) onRefresh();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.message || 'İçe aktarma başarısız.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    const inputClass = 'w-full px-4 py-2 bg-dark-bg border border-dark-border rounded-lg focus:outline-none focus:border-primary-500 text-dark-text';
    const labelClass = 'block text-sm font-medium text-dark-muted mb-1';

    return (
        <div className="fixed inset-0 z-[80] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-4">
            <div className="bg-dark-card border border-dark-border w-full max-w-2xl text-dark-text rounded-t-2xl shadow-2xl overflow-hidden flex flex-col max-h-[calc(100dvh-0.5rem)] sm:max-h-[90vh] sm:rounded-2xl">

                <div className="flex justify-between items-center p-5 sm:p-6 border-b border-dark-border bg-dark-bg/40">
                    <div>
                        <h2 className="text-xl font-bold">Tahsilatlardan İçe Aktar</h2>
                        <p className="text-sm text-dark-muted">Tahsilat kayıtlarındaki satıcı adlarından firma oluştur</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-dark-muted hover:text-dark-text hover:bg-dark-hover rounded-full transition-colors shrink-0">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-5 sm:p-6 border-b border-dark-border">
                    <div>
                        <label className={labelClass}>Para Birimi</label>
                        <select value={defaultCurrency} onChange={(e) => setDefaultCurrency(e.target.value)} className={`${inputClass} cursor-pointer`}>
                            {RECONCILIATION_CURRENCIES.map((currency) => (
                                <option key={currency} value={currency}>{currency}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {isLoading && (
                        <div className="flex items-center justify-center gap-2 p-10 text-dark-muted">
                            <Loader2 size={18} className="animate-spin" /> Yükleniyor...
                        </div>
                    )}

                    {!isLoading && candidates.length === 0 && (
                        <div className="p-10 text-center text-dark-muted">
                            İçe aktarılacak yeni satıcı bulunamadı.
                            <br />
                            <span className="text-xs">Tahsilat kayıtlarındaki tüm satıcılar zaten firma defterinde olabilir.</span>
                        </div>
                    )}

                    {!isLoading && candidates.length > 0 && (
                        <table className="w-full text-left text-sm">
                            <thead className="bg-dark-bg/60 text-dark-muted text-[11px] uppercase tracking-wider sticky top-0">
                                <tr>
                                    <th className="p-3 w-10">
                                        <input
                                            type="checkbox"
                                            checked={selected.size === candidates.length && candidates.length > 0}
                                            onChange={toggleAll}
                                            className="h-4 w-4 cursor-pointer accent-primary-500"
                                            aria-label="Tümünü seç"
                                        />
                                    </th>
                                    <th className="p-3">Firma Adı</th>
                                    <th className="p-3 hidden sm:table-cell">Tahsilat</th>
                                    <th className="p-3 hidden sm:table-cell">Son İşlem</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-dark-border">
                                {candidates.map((candidate) => (
                                    <tr
                                        key={candidate.name}
                                        onClick={() => toggleOne(candidate.name)}
                                        className="cursor-pointer hover:bg-dark-hover transition-colors"
                                    >
                                        <td className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={selected.has(candidate.name)}
                                                onChange={() => toggleOne(candidate.name)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="h-4 w-4 cursor-pointer accent-primary-500"
                                            />
                                        </td>
                                        <td className="p-3 font-medium text-dark-text">{candidate.name}</td>
                                        <td className="p-3 hidden sm:table-cell text-dark-muted">{candidate.collectionCount} kayıt</td>
                                        <td className="p-3 hidden sm:table-cell text-dark-muted">{formatRecordDate(candidate.lastCollectionAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="sticky bottom-0 z-10 p-4 sm:p-6 border-t border-dark-border bg-dark-card/95 flex items-center justify-between gap-3 pb-safe-offset">
                    <span className="text-sm text-dark-muted">{selected.size} firma seçildi</span>
                    <div className="flex gap-3">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-dark-muted hover:text-dark-text hover:bg-dark-hover transition-colors">
                            Vazgeç
                        </button>
                        <button
                            type="button"
                            onClick={handleImport}
                            disabled={isSubmitting || selected.size === 0}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Download size={16} /> {isSubmitting ? 'Aktarılıyor...' : 'İçe Aktar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImportSellersModal;
